import { useState } from "react";
import { toast } from "sonner";

import { useCreateIMROWriter, useCreateWriter, useUpdateWriter } from "@/api/mutations";
import { useGetMyProfile } from "@/api/queries";
import { formatDateForAPI, sendEvent, sendProperty } from "@/helpers/helpers";
import useAppStore from "@/store/appStateZustand";

import type { IMROWriterFormData } from "./useImroWriterForm";
import type { WriterFormData } from "./useWriterForm";

import useWriterStore from "../../songs/album/store/writerStore";

type Writer = {
  writercode?: string;
  writername?: string;
  firstname?: string;
  lastname?: string;
  middlename?: string;
  caeipinumber?: string;
  societycisaccode?: string;
  dateofbirth?: string;
  publishername?: string | unknown;
  publishercaeipi?: string | unknown;
  etag?: string;
};

type UseWriterMutationOptions = {
  onSuccess?: (writerData?: any) => void;
  onError?: (error: Error) => void;
  onValidationError?: (messages: string[]) => void;
};

export function useWriterMutation(options: UseWriterMutationOptions = {}) {
  const { onSuccess, onError, onValidationError } = options;
  const [pending, setPending] = useState(false);

  const selectedAccount = useAppStore(s => s.selectedAccount);
  const { addSelectedWriter, updateSelectedWriter, updateAvailableWriter } = useWriterStore();
  const createWriterMutation = useCreateWriter();
  const updateWriterMutation = useUpdateWriter();
  const createIMROWriterMutation = useCreateIMROWriter();
  const profile = useGetMyProfile();

  const createIMROWriter = async (data: IMROWriterFormData) => {
    setPending(true);

    try {
      // Handle date of birth formatting - supports both Date objects and DD/MM/YYYY strings
      let formattedDateOfBirth = "";
      if (data.dateofbirth != null) {
        if (data.dateofbirth instanceof Date) {
          // If it's a Date object from the date picker, format it to DD/MM/YYYY then to ISO
          const day = String(data.dateofbirth.getDate()).padStart(2, "0");
          const month = String(data.dateofbirth.getMonth() + 1).padStart(2, "0");
          const year = data.dateofbirth.getFullYear();
          const dateString = `${day}/${month}/${year}`;
          formattedDateOfBirth = formatDateForAPI(dateString) ?? "";
        }
        else if (typeof data.dateofbirth === "string" && data.dateofbirth.trim() !== "") {
          // If it's a string in DD/MM/YYYY format, format to ISO
          formattedDateOfBirth = formatDateForAPI(data.dateofbirth.trim()) ?? "";
        }
      }

      // CREATE mutation - matches legacy WritersForm addWriter logic
      const createData = {
        artistaccountcode: selectedAccount,
        firstname: data.firstname.trim(),
        lastname: data.lastname.trim(),
        country: data.country.trim(),
        countryofbirth: data.countryofbirth.trim(),

        // societycisaccode: 128, // IMRO specific code
        dateofbirth: formattedDateOfBirth,
        company: data.company?.trim(),
        emailaddress: data.emailaddress?.trim(),
        phonenumber: data.phonenumber?.trim(),
        pseudonym: data.pseudonym?.trim() ?? "",
        address1: data.address1.trim(),
        address2: data.address2?.trim() ?? "",
        address3: data.address3?.trim() ?? "",
        address4: data.address4?.trim() ?? "",
        address5: data.address5?.trim() ?? "",
        taxarea: data.taxarea?.trim() ?? "",

      };

      const result = await createIMROWriterMutation.mutateAsync(createData);

      // Check if the response indicates validation failure
      if (result.failedvalidation) {
        const messages = result.messages || ["IMRO registration failed due to validation errors"];
        onValidationError?.(messages);
        toast.error("IMRO registration failed due to validation errors. Please check the form and try again.");
        throw new Error(messages.join(", "));
      }

      if (result.model) {
        // Create display name from firstname/lastname if writername is not available
        let displayName = result.model.writername;
        if (!displayName || displayName.trim() === "") {
          const parts = [result.model.firstname, result.model.middlename, result.model.lastname].filter(Boolean);
          displayName = parts.join(" ");
        }

        const updatedWriter = {
          writercode: result.model.writercode,
          writername: displayName,
          caeipinumber: result.model.caeipinumber,
        };

        // Add the newly created writer to selected writers
        addSelectedWriter(updatedWriter);
        toast.success(`IMRO writer registered successfully: ${updatedWriter.writername}`);

        // Send HubSpot tracking events
        sendProperty({ property: "last_writer_added", value: "CURRENT_DATE", email: profile?.data?.email });
        sendEvent({ property: "pe14489857_writer_added" });

        onSuccess?.(result.model);
        return result;
      }
      else {
        throw new Error("Failed to create IMRO writer - no model returned");
      }
    }
    catch (error) {
      console.error("Error creating IMRO writer:", error);

      let errorMessage = "IMRO registration failed. Please try again.";
      let isValidationError = false;

      if (error instanceof Error && error.message) {
        // Check if the error message contains multiple validation messages (separated by ", ")
        if (error.message.includes(", ") && error.message.length > 50) {
          // Likely validation errors joined by processRes
          isValidationError = true;
          const messages = error.message.split(", ");
          onValidationError?.(messages);
          toast.error("IMRO registration failed due to validation errors. Please check the form and try again.");
        }
        else {
          // Single error message or general error
          errorMessage = error.message;

          // Check for specific error types
          if (errorMessage.includes("validation") || errorMessage.includes("required") || errorMessage.includes("invalid")) {
            isValidationError = true;
            onValidationError?.([errorMessage]);
            toast.error("IMRO registration failed due to validation errors. Please check the form and try again.");
          }
        }
      }

      if (!isValidationError) {
        // Show toast for general errors with more user-friendly messages
        let userFriendlyMessage = errorMessage;

        if (errorMessage.includes("server error")) {
          userFriendlyMessage = "Server error occurred. Please try again later.";
        }
        else if (errorMessage.includes("network") || errorMessage.includes("fetch")) {
          userFriendlyMessage = "Network error. Please check your connection and try again.";
        }
        else if (errorMessage === "Failed to create IMRO writer - no model returned") {
          userFriendlyMessage = "IMRO registration completed but no writer data was returned. Please contact support.";
        }

        toast.error(`IMRO registration failed: ${userFriendlyMessage}`);
        onError?.(new Error(userFriendlyMessage));
      }

      throw error;
    }
    finally {
      setPending(false);
    }
  };

  const createWriter = async (
    data: WriterFormData,
    isUSPro: boolean,
  ) => {
    setPending(true);

    try {
      // Handle date of birth formatting - matches legacy logic
      let formattedDateOfBirth = "";
      if (data.dateofbirth != null && data.dateofbirth.trim() !== "") {
        // For now, we expect dateofbirth to be in ISO format already
        // In legacy, it uses moment to format from year/month/day components
        formattedDateOfBirth = data.dateofbirth.trim();
      }

      // CREATE mutation - matches legacy WritersForm addWriter logic
      const createData = {
        artistaccountcode: selectedAccount,
        firstname: data.firstname.trim(),
        lastname: data.lastname.trim(),
        middlename: (data.middlename != null && data.middlename.trim() !== "") ? data.middlename.trim() : "",
        caeipinumber: (data.caeipinumber != null && data.caeipinumber.trim() !== "") ? data.caeipinumber.trim() : "",
        societycisaccode: (data.societycisaccode != null && data.societycisaccode.trim() !== "") ? data.societycisaccode.trim() : "",
        dateofbirth: formattedDateOfBirth,
        // Publisher fields: use empty string for creates when not US PRO (legacy pattern)
        publishername: isUSPro ? ((data.associatedPublisherName != null && data.associatedPublisherName.trim() !== "") ? data.associatedPublisherName.trim() : "") : "",
        publishercaeipi: isUSPro ? ((data.associatedPublisherCAEIPI != null && data.associatedPublisherCAEIPI.trim() !== "") ? data.associatedPublisherCAEIPI.trim() : "") : "",
        isunknownwriter: false,
      };

      const result = await createWriterMutation.mutateAsync(createData);

      if (result.model) {
        // Create display name from firstname/lastname if writername is not available
        let displayName = result.model.writername;
        if (!displayName || displayName.trim() === "") {
          const parts = [result.model.firstname, result.model.middlename, result.model.lastname].filter(Boolean);
          displayName = parts.join(" ");
        }

        const updatedWriter = {
          writercode: result.model.writercode,
          writername: displayName,
          caeipinumber: result.model.caeipinumber,
        };

        // Add the newly created writer to selected writers
        addSelectedWriter(updatedWriter);
        toast.success(`Writer created: ${updatedWriter.writername}`);

        // Send HubSpot tracking events
        sendProperty({ property: "last_writer_added", value: "CURRENT_DATE", email: profile?.data?.email });
        sendEvent({ property: "pe14489857_writer_added" });

        onSuccess?.();
        return result;
      }
      else {
        throw new Error("Failed to create writer - no model returned");
      }
    }
    catch (error) {
      console.error("Error creating writer:", error);

      // Check if this is a validation error (412) with messages
      if (error instanceof Error && error.message && onValidationError) {
        // The error message from processRes contains the joined messages
        // Split them back into an array for consistent handling
        const messages = error.message.split(", ");
        onValidationError(messages);
      }
      else {
        // Generic error handling
        toast.error("Failed to create writer");
        onError?.(error as Error);
      }
      throw error;
    }
    finally {
      setPending(false);
    }
  };

  const updateWriter = async (
    data: WriterFormData,
    isUSPro: boolean,
    editingWriterCode: string,
    editingWriter: Writer | null | undefined,
    editingWriterFromAvailable: Writer | null,
    writerQueryData: Writer | undefined,
  ) => {
    setPending(true);

    try {
      // Handle date of birth formatting - matches legacy logic
      let formattedDateOfBirth = "";
      if (data.dateofbirth != null && data.dateofbirth.trim() !== "") {
        // For now, we expect dateofbirth to be in ISO format already
        // In legacy, it uses moment to format from year/month/day components
        formattedDateOfBirth = data.dateofbirth.trim();
      }

      // Get original date of birth for updates (if date wasn't changed)
      const originalDateOfBirth = editingWriter && "dateofbirth" in editingWriter
        ? (editingWriter.dateofbirth ?? "")
        : "";

      // UPDATE mutation - matches legacy WritersForm update logic
      // Use the most current etag - prioritize API data, then availableWriters
      let currentEtag;
      if (writerQueryData && "etag" in writerQueryData) {
        currentEtag = writerQueryData.etag;
      }
      else if (editingWriterFromAvailable && "etag" in editingWriterFromAvailable) {
        currentEtag = editingWriterFromAvailable.etag;
      }

      const updateData = {
        artistaccountcode: selectedAccount,
        writercode: editingWriterCode,
        firstname: data.firstname.trim(),
        lastname: data.lastname.trim(),
        middlename: (data.middlename != null && data.middlename.trim() !== "") ? data.middlename.trim() : "",
        caeipinumber: (data.caeipinumber != null && data.caeipinumber.trim() !== "") ? data.caeipinumber.trim() : "",
        societycisaccode: (data.societycisaccode != null && data.societycisaccode.trim() !== "") ? data.societycisaccode.trim() : "",
        // Use new formatted date if provided, otherwise keep original (legacy pattern: formattedDOB || apiDOB)
        dateofbirth: formattedDateOfBirth || originalDateOfBirth,
        // Publisher fields: use null for updates when not US PRO (legacy pattern)
        publishername: isUSPro ? ((data.associatedPublisherName != null && data.associatedPublisherName.trim() !== "") ? data.associatedPublisherName.trim() : "") : null,
        publishercaeipi: isUSPro ? ((data.associatedPublisherCAEIPI != null && data.associatedPublisherCAEIPI.trim() !== "") ? data.associatedPublisherCAEIPI.trim() : "") : null,
        // For updates, we need to include the etag for version control - use the most current one
        etag: currentEtag,
      };

      // Debug logging for etag issues
      if (currentEtag == null || currentEtag === "") {
        console.warn("No etag available for update:", {
          editingWriterCode,
          hasApiData: !!writerQueryData,
          hasAvailableWriter: !!editingWriterFromAvailable,
          apiDataEtag: writerQueryData && "etag" in writerQueryData ? writerQueryData.etag : "none",
          availableWriterEtag: editingWriterFromAvailable && "etag" in editingWriterFromAvailable ? editingWriterFromAvailable.etag : "none",
        });
      }

      const result = await updateWriterMutation.mutateAsync(updateData);

      if (result.model) {
        // Create display name from firstname/lastname if writername is not available
        let displayName = result.model.writername;
        if (!displayName || displayName.trim() === "") {
          const parts = [result.model.firstname, result.model.middlename, result.model.lastname].filter(Boolean);
          displayName = parts.join(" ");
        }

        const updatedSelectedWriter = {
          writercode: result.model.writercode,
          writername: displayName,
          caeipinumber: result.model.caeipinumber,
        };

        const updatedAvailableWriter = {
          writercode: result.model.writercode,
          writername: displayName,
          firstname: result.model.firstname || "",
          lastname: result.model.lastname || "",
          middlename: result.model.middlename,
          caeipinumber: result.model.caeipinumber,
          societycisaccode: result.model.societycisaccode,
          dateofbirth: result.model.dateofbirth,
          verifiedstate: typeof result.model.verifiedstate === "string" ? result.model.verifiedstate : "",
          writercontrolledworkscount: typeof result.model.writercontrolledworkscount === "number" ? result.model.writercontrolledworkscount : 0,
          changespending: Boolean(result.model.changespending),
        };

        // Update both stores
        updateSelectedWriter(editingWriterCode, updatedSelectedWriter);
        updateAvailableWriter(editingWriterCode, updatedAvailableWriter);
        toast.success(`Writer updated: ${updatedSelectedWriter.writername}`);

        onSuccess?.();
        return result;
      }
      else {
        throw new Error("Failed to update writer - no model returned");
      }
    }
    catch (error) {
      console.error("Error updating writer:", error);

      // Check if this is a validation error (412) with messages
      if (error instanceof Error && error.message && onValidationError) {
        // The error message from processRes contains the joined messages
        // Split them back into an array for consistent handling
        const messages = error.message.split(", ");
        onValidationError(messages);
      }
      else {
        // Generic error handling
        toast.error("Failed to update writer");
        onError?.(error as Error);
      }
      throw error;
    }
    finally {
      setPending(false);
    }
  };

  return {
    createWriter,
    updateWriter,
    createIMROWriter,
    pending,
  };
}
